/**
 * Cleans string values by trimming whitespace and normalizing multiple spaces to single spaces.
 *
 * @param {Array|Object|string|*} data - The input data to clean
 * @returns {Array|Object|string|*} A new copy of the data with all string values cleaned:
 *   - If array: returns a new array with all string elements trimmed and spaces normalized
 *   - If object: returns a new object with all string properties trimmed and spaces normalized
 *   - If string: returns the trimmed string with normalized spaces
 *   - Otherwise: returns the original data unchanged
 *
 * @example
 * // For arrays
 * trimAndCleanSpaces(["  hello  world  ", 123]) // ["hello world", 123]
 *
 * // For objects
 * trimAndCleanSpaces({ name: "  John   Doe  ", age: 30 }) // { name: "John Doe", age: 30 }
 *
 * // For strings
 * trimAndCleanSpaces("  test  space  ") // "test space"
 *
 * // For other primitives
 * trimAndCleanSpaces(123) // 123 (unchanged)
 */
export function trimAndCleanSpaces(data) {
    if (Array.isArray(data)) {
        return data.map((value) =>
            typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : value
        );
    } else if (typeof data === 'object' && data !== null) {
        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : value
            ])
        );
    } else if (typeof data === 'string') {
        return data.trim().replace(/\s+/g, ' ');
    }
    return data; // Return as-is if not an object, array or string
}
